import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, CheckCircle2, Loader2, ShieldCheck, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicUpload() {
  const { token } = useParams();
  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [docType, setDocType] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/public/applications/${token}`)
    .then((r) => { setApp(r.data); setDocType(r.data.required_documents?.[0] || "Other"); })
    .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired."));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType); fd.append("file", file);
      const { data } = await axios.post(`${API}/public/applications/${token}/documents`, fd);
      setApp({ ...app, uploaded: data.uploaded });
      setFile(null);
      toast.success("Document uploaded — thank you");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-secondary/30">
      <Card className="p-8 max-w-md text-center"><p className="text-red-600 font-medium">{error}</p></Card>
    </div>
  );
  if (!app) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const brand = app.primary_color || "#166534";
  const uploadedTypes = new Set((app.uploaded || []).map((u) => u.doc_type));

  return (
    <div className="min-h-screen bg-secondary/30 py-10 px-4" style={{ "--brand-c": brand }}>
      <Toaster position="top-right" richColors />
      <div className="max-w-xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          {app.logo_url ? <img src={app.logo_url} alt="logo" className="h-10 w-10 object-contain" />
            : <div className="h-10 w-10 rounded-md flex items-center justify-center text-white" style={{ background: brand }}><ShieldCheck className="h-5 w-5" /></div>}
          <div>
            <p className="font-heading font-bold">{app.institution}</p>
            <p className="text-xs text-muted-foreground">Admissions — Document Upload</p>
          </div>
        </div>

        <Card className="p-6 space-y-1">
          <h1 className="font-heading text-2xl font-bold">Hello {app.applicant_name}</h1>
          <p className="text-muted-foreground text-sm">Please upload your supporting documents for <strong>{app.programme}</strong>.</p>
        </Card>

        <Card className="p-6">
          <p className="text-sm font-semibold mb-3">Required documents</p>
          <div className="space-y-2" data-testid="public-required">
            {(app.required_documents || []).map((d) => (
              <div key={d} className="flex items-center gap-2 text-sm">
                {uploadedTypes.has(d) ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
                <span className={uploadedTypes.has(d) ? "text-foreground" : "text-muted-foreground"}>{d}</span>
                {uploadedTypes.has(d) && <span className="text-[11px] text-emerald-600">uploaded</span>}
              </div>
            ))}
          </div>
        </Card>

        {app.locked ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">This application is closed and no longer accepts documents.</Card>
        ) : (
          <Card className="p-6 space-y-3">
            <p className="text-sm font-semibold">Upload a document</p>
            <div>
              <Label className="text-xs">Document type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="public-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[...(app.required_documents || []), "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <input data-testid="public-file" type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:text-white file:px-3 file:py-1.5 file:text-sm cursor-pointer" />
            <Button data-testid="public-upload" onClick={submit} disabled={busy} className="text-white w-full" style={{ background: brand }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Upload document</>}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Accepted: PDF, Word (.docx), or text. Your documents go straight to the admissions office.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
